import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Barnet\'s AMBER DfT rating mean the council will pay my claim?',
    answer: 'Not automatically. AMBER means Barnet performs below the best authorities on combined condition, spend and best-practice scorecards — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Barnet\'s June 2025 transparency report records 4,611 pothole repairs in 2024/25, projects 4,500 in 2025/26, and shows volatile condition swings on principal roads. Those figures can support a claim when tied to your specific road and defect — they do not guarantee success on their own.',
  },
  {
    question: 'Barnet has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because Barnet\'s projected 2025/26 capital spend is £26.186m against a DfT allocation of £1.198m — more than twenty times the grant. Section 58 is about the individual defect, not aggregate budgets. Overall and condition ratings are both AMBER, and best practice is AMBER.',
  },
  {
    question: 'Why did pothole repairs rise 39% despite 89% preventative spending?',
    answer: 'Barnet filled 3,320 potholes in 2021/22 and 4,611 in 2024/25 — a 39% rise while preventative maintenance share climbed from 79% to 89%. The council\'s own report states approximately 50% of the reactive budget goes to pothole repairs or extensive patching. Rising patch counts alongside maximum preventative percentages indicate defects still form across London\'s largest borough network — not proof your specific defect was unavoidable, but evidence of sustained reactive workload.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 592km — 82% of Barnet\'s 723km carriageway network. The council publishes RED-condition percentages for U-roads only: 11% in 2022, 5% in 2023, and 12% in 2024. That means roughly 71km of residential network was in RED condition at the latest survey. Most Barnet pothole damage happens on these streets. Prior reports via the council portal and photos showing defect age matter more than network-wide averages.',
  },
  {
    question: 'Is Barnet responsible for the A41, North Circular or M1?',
    answer: 'No. Barnet\'s pothole reporting guidance states the A41 and A406 (North Circular) are maintained by Transport for London, and the M1 by National Highways. Private roads are the responsibility of their owners. If your incident was on a Barnet-maintained street, your claim goes to London Borough of Barnet — check the Barnet Highways Register if unsure.',
  },
  {
    question: 'What does the 2023/24 network reassessment mean for my claim?',
    answer: 'Barnet reassessed its highway asset in 2023/24 and found Barnet-maintained highways had increased by over 90km (13%) compared with the previous understanding — carriageway length alone grew approximately 30km (4%) to 723km. The council states this was aligned to CIPFA valuation methodology. For a claim, the point is whether your specific road was on an inspection and maintenance programme — not whether the asset register grew — but the reassessment shows maintenance planning was previously based on an incomplete picture of the network.',
  },
  {
    question: 'How do I report a pothole to Barnet Council?',
    answer: 'Report road or pavement damage online at barnet.gov.uk. The council asks for a map pin, photo if available, and your contact details; non-urgent reports are usually investigated within 10 working days. Urgent damage should be reported by calling 020 8359 2000. Prior reports of the same defect strengthen a claim by demonstrating notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BarnetCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Barnet Pothole Claims | 4,611 Repairs on London's Largest Network | Fixtyer"
        description="Barnet Council earns AMBER DfT ratings on overall performance and condition, GREEN on spend. It patched 4,611 potholes in 2024/25 on a 723km network — London's largest — while claiming 89% preventative spending. What that means for your claim."
        canonical="/councils/barnet"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Barnet', url: '/councils/barnet' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="London Borough of Barnet"
        councilSlug="barnet"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/barnet"
        headline="Barnet Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Barnet</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            4,611 Pothole Repairs in 2024/25 on London's Largest Road Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Barnet earns <strong className="text-amber-400">AMBER</strong> DfT scorecards on overall performance and
            condition, <strong className="text-emerald-400">GREEN</strong> on spend — yet its June 2025 transparency
            report records <strong className="text-rose-400">4,611 pothole repairs in 2024/25</strong>, up 39% from
            2021/22, on a <strong className="text-white">723km network</strong> carrying London's heaviest principal-road
            traffic. Preventative spending is projected at <strong className="text-amber-400">89%</strong> for 2025/26
            while U-road RED condition doubled from 5% to 12% in one survey cycle. Section 58 still turns on your
            specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">723km</div>
            <div className="text-white font-medium mt-2">Carriageway — largest in London</div>
            <div className="text-brand-300 text-sm mt-1">
              592km unclassified residential roads (82%), principal roads at 50,000 average daily vehicle movements
              and 1,000 HGV movements — both highest in London per the council's report.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport Scorecards 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Official ratings from the DfT local road maintenance publication — Barnet is one of 125 amber-rated authorities
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Barnet DfT road maintenance scorecards 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left font-semibold text-brand-900">Scorecard</th>
                  <th className="px-6 py-4 text-center font-semibold text-brand-900">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Wider best practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Condition data caveat:</strong> Barnet's transparency report states that for 2020 and 2021,
              condition data was impacted by Transport for London stopping and subsequently changing condition surveys
              in London. Published RAG percentages begin from 2022. Year-on-year swings — such as A-road green condition
              falling from 74% in 2023 to 52% in 2024 — should be read in that context.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            723km of Roads — London's Largest Borough Network
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Barnet's transparency report published 23 June 2025
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">723km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">Largest in London</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">592km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">82% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">75km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">96km principal roads total</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">56km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Barnet highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways (including footpaths)</td>
                  <td className="px-6 py-4 text-center font-medium">1,290km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">41km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">27km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures (72 road bridges)</td>
                  <td className="px-6 py-4 text-center font-medium">78 structures — £56.7m GRC</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Asset valuation (carriageway and footway)</td>
                  <td className="px-6 py-4 text-center font-medium">£1.9bn (up from £1.6bn)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Barnet's principal roads carry the highest traffic volumes in all London at 50,000 average daily vehicle movements. Barnet's principal roads carry the highest volume of HGVs in all London at 1,000 average daily vehicle movements."
            source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Annual AI condition surveys — with 2020/21 data gaps from TfL survey changes
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Survey caveat:</strong> Barnet commissions an annual AI condition survey augmented by visual
              assessment. The council states 2020 and 2021 condition figures were impacted by TfL stopping and
              subsequently changing condition surveys in London. From 2026/27, PAS 2161 will replace the current
              three-category RAG system with five condition categories nationally.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (75km)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                    <td className="px-4 py-2 text-center">44%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center text-emerald-600">74%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12%</td>
                    <td className="px-4 py-2 text-center font-bold">36%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">52%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Green-rated A-roads fell 22 percentage points in one year — from 74% in 2023 to 52% in 2024. RED
              condition rose from 10% to 12% in the same period.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (56km)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">36%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">55%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">14%</td>
                    <td className="px-4 py-2 text-center font-bold">37%</td>
                    <td className="px-4 py-2 text-center font-bold">49%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              14% of B/C roads were in RED condition in 2024 — roads the council's own survey classifies as
              "should be considered for maintenance."
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (592km) — RED condition only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">11%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-emerald-600">5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED condition more than doubled from 5% in 2023 to 12% in 2024 — roughly 71km of residential
              network officially needing substantial maintenance at the latest survey.
            </p>
          </div>

          <QuoteCard
            quote="Compared to the previous understanding of the network (pre 2023/24), Barnet-maintained highways have increased significantly by over 90km (13%)."
            source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — capital investment tripled since 2020/21, yet pothole patch counts remain elevated
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Barnet highway maintenance spending 2020 to 2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">1,198</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">26,186</td>
                  <td className="px-4 py-3 text-center">3,233</td>
                  <td className="px-4 py-3 text-center text-emerald-400">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">368</td>
                  <td className="px-4 py-3 text-center font-bold">27,024</td>
                  <td className="px-4 py-3 text-center">3,478</td>
                  <td className="px-4 py-3 text-center">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">256</td>
                  <td className="px-4 py-3 text-center">20,213</td>
                  <td className="px-4 py-3 text-center">3,094</td>
                  <td className="px-4 py-3 text-center">87%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">12,474</td>
                  <td className="px-4 py-3 text-center">4,018</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">11,544</td>
                  <td className="px-4 py-3 text-center">3,068</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">10,308</td>
                  <td className="px-4 py-3 text-center">2,062</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend rose from £10.3m in 2020/21 to £27.0m in 2024/25, backed by a £97m capital allocation
                approved in March 2023 for the Improving Barnet's Roads and Pavements programme. Projected 2025/26
                capital spend of £26.2m is more than twenty times the £1.198m DfT allocation.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Even with 89% preventative spend, the council states approximately 50% of the reactive budget goes
                to pothole repairs or extensive patching. Total highways spend does not prove every defect was caught
                within inspection intervals on a 723km network where U-road RED condition jumped to 12%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Barnet's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Barnet estimated potholes filled 2021 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,320</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,668</td>
                  <td className="px-6 py-4 text-center text-amber-600">+10.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,694</td>
                  <td className="px-6 py-4 text-center text-rose-600">+41.4%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,611</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+38.9%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">4,500</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Approximately 50% of the reactive budget is spent on pothole repairs or more extensive patching of the carriageway."
              source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Roughly twelve patches a day</h3>
              <p className="text-surface-600 text-sm">
                4,611 pothole repairs in 2024/25 works out to roughly 12.6 patches per day across 723km. A council
                claiming 89% preventative spending while filling that volume of safety-critical defects is one where
                individual potholes routinely form between resurfacing schemes — especially on 592km of U-roads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Barnet says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and programme selection</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Annual AI condition survey augmented by visual assessment and deterioration modelling</li>
                <li>• Independent condition survey informs the Improving Barnet's Roads and Pavements programme</li>
                <li>• 2025/26 plan: 12 miles of carriageway resurfacing and 3.4 miles of footway relay</li>
                <li>• Council contributing to DfT PAS 2161 development for future AI-led monitoring</li>
                <li>• Trialling Thermal Road Repair, Multihog, Roadmender and Pothole Pro for reactive repairs</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Statutory duty</h3>
              <p className="text-surface-600 text-sm mb-3">
                Barnet cites Section 41 of the Highways Act 1980 as imposing a duty to maintain highways maintainable
                at public expense. The council adopted a Highways Infrastructure Asset Management Plan in 2022 and a
                Highway Investment Strategy in March 2024, targeting planned over reactive maintenance.
              </p>
              <p className="text-surface-600 text-sm">
                In 2025/26 the council forecasts circa £3.75m reactive versus circa £16.5m planned — a 23% reactive
                share, within the council's stated typical 20% to 25% differential.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Barnet must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show
              a reasonable system for inspecting and repairing the specific defect — not just publish an amber DfT
              scorecard or cite £97m of planned investment.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual independent condition survey — and when was it last assessed?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports via the council portal giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="The London Borough of Barnet highway network is the largest, most valuable, and most visible community asset and is probably the most used of all our services, used by nearly all residents daily."
            source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Barnet says it will deliver this financial year — Cabinet approved 5 February 2025
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">12 miles</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway resurfacing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">3.4 miles</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footway relay</div>
              <div className="text-xs text-surface-500 mt-1">Plus 42 footway patching schemes</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">2</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Major structure works</div>
              <div className="text-xs text-surface-500 mt-1">Sanders Lane East and Devonshire Road bridges</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~4,500</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
          </div>

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Twelve miles of carriageway resurfacing treats roughly 2.7% of Barnet's 723km network in a single year.
              The remaining 97%+ relies on reactive patching, routine inspections and the annual condition survey to
              catch deterioration — on a network where U-road RED condition already sits at 12% and principal roads
              carry London's heaviest traffic.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Barnet Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the transparency report published 23 June 2025
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On network scale and reassessment</h3>
              <QuoteCard
                quote="Compared to the previous understanding of the network (pre 2023/24), Barnet-maintained highways have increased significantly by over 90km (13%). Discounting footpaths, Barnet's carriageway network (723km) alone has grown approximately 30km (4%) and is one of the largest in London."
                source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive maintenance spend</h3>
              <QuoteCard
                quote="Approximately 50% of the reactive budget is spent on pothole repairs or more extensive patching of the carriageway. Of the remaining 50%, the majority of this is spent on footway patching, with less than 5% spent on other items such as bollards and pedestrian guard railing."
                source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On resident enquiries</h3>
              <QuoteCard
                quote="In 2024/25, the Highways service received 19,202 resident enquiries on a range of topics. 92.7% of resident enquiries were responded to within 10 working days."
                source="London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Barnet invests heavily — but condition volatility and patch volumes tell a mixed story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £26.2m projected capital vs £1.198m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £97m Improving Barnet's Roads programme from March 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 89% preventative spend in 2024/25 and projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual AI condition surveys and documented asset management plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 12 miles carriageway resurfacing planned for 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-resourced Section 58 defence citing surveys, portal reports and the £97m investment programme.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and condition scorecards from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 592km of U-roads — 82% of network — with RED condition at 12%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road green condition fell 22 points in one year (74% to 52%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,611 pothole patches in 2024/25 — up 39% since 2021/22
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 50% of reactive budget consumed by pothole repairs and patching
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Network reassessment found 90km more highways than previously understood
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and a £97m resurfacing programme, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole via the council portal — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, 12% RED condition and 592km of residential network is structural context</li>
              <li>• Whether your street was on the 2025/26 programmed works list or outside the 2.7% resurfacing coverage</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Barnet's own
              transparency data — including the prevention-spending versus pothole-volume pattern — where they help you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Barnet Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. Non-urgent reports are usually investigated within
            10 working days; call 020 8359 2000 for urgent damage. Note: the A41, A406 and M1 are not Barnet-maintained.
          </p>
          <a
            href="https://www.barnet.gov.uk/your-council/contact-council/report-it-now/report-pothole-or-damage-road-or-pavement"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report road or pavement damage — barnet.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Barnet?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            London's largest network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration context</li>
                <li>• No prior-report search</li>
                <li>• No prevention-paradox data cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 12% U-road RED condition documented</li>
                <li>✅ 4,611 pothole repairs in 2024/25 cited</li>
                <li>✅ 89% prevention vs rising patch counts argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Barnet</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=barnet"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Barnet Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.barnet.gov.uk/sites/default/files/2025-06/LBB%20Local%20Highways%20maintenance%20transparency%20report%202025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Barnet Local Highways Maintenance Transparency Report (23 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
